import { useRef, useState } from 'react';
import { api, getPinUnlockToken } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';
import useTts from '../lib/useTts';
import { Camera, ImagePlus, Loader2, Volume2, Mic, Square, RotateCcw, Star, Trophy, Image as ImageIcon, Type } from 'lucide-react';
import { toast } from 'sonner';
import ImageCropper from '../components/ImageCropper';
import PinGateModal from '../components/PinGateModal';

async function fileToDataUrl(file, maxSize = 1600) {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf], { type: file.type });
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  return c.toDataURL('image/jpeg', 0.9);
}

export default function QuickPractice() {
  const { activeChild, uiLang } = useApp();
  const t = useT(uiLang);
  const speed = activeChild?.tts_speed ?? 0.75;
  const tts = useTts(speed);
  const [text, setText] = useState('');
  const [pageImg, setPageImg] = useState(null);
  const [language, setLanguage] = useState(activeChild?.languages?.[0] || 'en');
  const [extracting, setExtracting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showImage, setShowImage] = useState(false);
  const [pendingCrop, setPendingCrop] = useState(null);
  const [pinReady, setPinReady] = useState(!!getPinUnlockToken());
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const onFile = async (file) => {
    if (!file) return;
    const src = await fileToDataUrl(file);
    setPendingCrop(src);
  };

  const finishCrop = async (b64) => {
    setPendingCrop(null); setExtracting(true); setPageImg(b64);
    try {
      const { data } = await api.post('/ocr', { image_base64: b64, language });
      setText(data.text);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const isBlock = e?.response?.status === 400 && /image can't be uploaded/i.test(String(detail || ''));
      toast.error(isBlock ? detail : 'OCR failed');
      if (isBlock) setPageImg(null);
    }
    finally { setExtracting(false); }
  };

  const playTTS = () => tts.play(text, { language, speed });
  const playWord = (w) => tts.play(w, { language, speed: Math.min(speed, 0.7) });

  const startMic = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
    chunksRef.current = [];
    mr.ondataavailable = e => { if (e.data.size) chunksRef.current.push(e.data); };
    mr.onstop = async () => {
      stream.getTracks().forEach(tk => tk.stop());
      setProcessing(true);
      try {
        const form = new FormData();
        form.append('audio', new Blob(chunksRef.current, { type: 'audio/webm' }), 'read.webm');
        form.append('language', language);
        form.append('prompt', text);
        const stt = await api.post('/stt', form);
        const { data } = await api.post('/sessions', { child_id: activeChild.child_id, language, original_text: text, spoken_text: stt.data.text || '' });
        setResult(data);
        tts.play(data.feedback, { language, speed });
      } catch { toast.error('Scoring failed'); }
      finally { setProcessing(false); }
    };
    mediaRef.current = mr; mr.start(); setRecording(true); setResult(null);
  };
  const stopMic = () => { if (mediaRef.current?.state !== 'inactive') mediaRef.current.stop(); setRecording(false); };

  const words = result?.marked_words || text.split(/(\s+)/).map(w => ({ word: w, status: w.trim() ? 'default' : 'space' }));
  const missedWords = result ? Array.from(new Set((result.missed_words || []).map(w => w.replace(/[.,!?।"]+$/g, '')).filter(Boolean))) : [];

  return (
    <div className="px-6 pt-6 pb-36 max-w-3xl mx-auto space-y-4 overflow-x-hidden">
      {!pinReady && (
        <PinGateModal
          onUnlocked={() => setPinReady(true)}
          onCancel={() => window.history.back()}
        />
      )}
      <h1 className="text-3xl font-extrabold">{t('quickPractice')}</h1>

      {!text && (
        <div className="flex gap-3 flex-wrap">
          <label data-testid="quick-camera-btn" className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-6 py-4 rounded-full inline-flex items-center gap-2 cursor-pointer">
            {extracting ? <Loader2 className="animate-spin" size={22}/> : <Camera size={22} strokeWidth={3}/>}
            {extracting ? t('extractingText') : t('fromCamera')}
            <input type="file" accept="image/*" capture="environment" onChange={e => onFile(e.target.files?.[0])} className="hidden"/>
          </label>
          <label data-testid="quick-gallery-btn" className="pushable bg-white border-2 border-slate-200 text-slate-700 font-bold px-6 py-4 rounded-full inline-flex items-center gap-2 cursor-pointer">
            <ImagePlus size={22} strokeWidth={3}/> {t('fromGallery')}
            <input type="file" accept="image/*" onChange={e => onFile(e.target.files?.[0])} className="hidden"/>
          </label>
        </div>
      )}

      {text && (
        <>
          <div className="flex justify-end">
            <button data-testid="quick-toggle-view" onClick={() => setShowImage(v => !v)} className="pushable bg-white border-2 border-slate-200 font-bold px-3 py-2 rounded-full inline-flex items-center gap-1 text-sm">
              {showImage ? <><Type size={16} strokeWidth={3}/> {t('viewText')}</> : <><ImageIcon size={16} strokeWidth={3}/> {t('viewPage')}</>}
            </button>
          </div>
          {showImage && pageImg && (
            <div className="bg-white rounded-3xl p-3">
              <img src={pageImg} alt="page" className="w-full max-h-[420px] object-contain rounded-2xl" style={{ filter: 'contrast(1.1) brightness(1.05) saturate(1.05)' }}/>
            </div>
          )}
          <div className="bg-white rounded-3xl p-8">
            <p data-testid="quick-reading-text" className={`text-2xl md:text-3xl font-bold leading-relaxed text-center whitespace-pre-wrap ${language==='hi'?'font-hi':''}`}>
              {words.map((w, i) => {
                const cls = w.status==='correct'?'word-correct':w.status==='missed'?'word-missed cursor-pointer':'word-default';
                if (w.status === 'missed') return <span key={i} className={cls} onClick={() => playWord(w.word.replace(/[.,!?।"]+$/g, ''))}>{w.word}</span>;
                return <span key={i} className={cls}>{w.word}</span>;
              })}
            </p>
          </div>

          {!result ? (
            <div className="flex justify-center gap-3 flex-wrap">
              <button onClick={playTTS} className="pushable bg-[#D97398] text-[#2B2140] font-bold px-6 py-4 rounded-full inline-flex items-center gap-2">
                <Volume2 size={22} strokeWidth={3}/> {t('readToMe')}
              </button>
              {!recording ? (
                <button data-testid="quick-mic-start" onClick={startMic} disabled={processing} className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-8 py-4 rounded-full inline-flex items-center gap-2 relative">
                  {processing ? <Loader2 className="animate-spin" size={22}/> : <Mic size={24} strokeWidth={3}/>} {processing ? t('scoring') : t('yourTurn')}
                </button>
              ) : (
                <button data-testid="quick-mic-stop" onClick={stopMic} className="pushable bg-red-500 text-white font-bold px-8 py-4 rounded-full inline-flex items-center gap-2 relative recording-pulse">
                  <Square size={22} strokeWidth={3}/> {t('stopMic')}
                </button>
              )}
            </div>
          ) : (
            <div className="bg-[#F4B740] rounded-3xl p-6">
              <div className="text-center">
                <Trophy size={48} className="text-amber-500 mx-auto mb-3"/>
                <p className={`text-xl font-bold mb-4 ${language==='hi'?'font-hi':''}`}>{result.feedback}</p>
                <div className="flex justify-center gap-4 mb-4 flex-wrap">
                  <div className="bg-white rounded-2xl px-6 py-3">
                    <div className="text-xs font-bold text-slate-500 uppercase">{t('accuracy')}</div>
                    <div className="text-3xl font-extrabold text-green-600">{result.accuracy}%</div>
                  </div>
                  <div className="bg-white rounded-2xl px-6 py-3 flex items-center gap-2">
                    <Star size={24} className="text-amber-500 fill-amber-400"/>
                    <div>
                      <div className="text-xs font-bold text-slate-500 uppercase">{t('points')}</div>
                      <div className="text-3xl font-extrabold">+{result.points}</div>
                    </div>
                  </div>
                </div>
              </div>
              {missedWords.length > 0 && (
                <div className="mt-3 bg-white/70 rounded-2xl p-4">
                  <div className="text-sm font-bold text-slate-600 mb-1">{t('practiceWords')}</div>
                  <div className="text-xs text-slate-400 mb-3">{t('tapToHear')}</div>
                  <div className="flex flex-wrap gap-2">
                    {missedWords.map((w, i) => (
                      <button key={i} onClick={() => playWord(w)} className={`pushable bg-[#F4B740] text-[#B45309] font-bold px-4 py-2 rounded-full inline-flex items-center gap-2 ${language==='hi'?'font-hi':''}`}>
                        <Volume2 size={16} strokeWidth={3}/> {w}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-center mt-4">
                <button onClick={() => setResult(null)} className="pushable bg-white font-bold px-5 py-3 rounded-full inline-flex items-center gap-2">
                  <RotateCcw size={18}/> {t('tryAgain')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {pendingCrop && <ImageCropper src={pendingCrop} onCancel={() => setPendingCrop(null)} onCropped={finishCrop}/>}
    </div>
  );
}
